import React, { useMemo } from 'react';
// Typography/Chip: this shared expanded-row view needs pixel-precise custom styling (fontSize/
// fontWeight/color combinations per field type) that the closed DS Typography/Chip APIs can't
// express — same documented exception pattern as other grid-data internals.
// eslint-disable-next-line no-restricted-syntax
import { Box, Typography, Grid, Chip, Stack, useMediaQuery, Theme, useTheme, Avatar } from '@mui/material';
import { ActionDefinition, ColumnDefinition } from '@sm-dashboard/types';
import { getCampaignGroupInitials } from '@sm-dashboard/formatting';
import { ExpandedViewLayout, StandartItem, InfoItem } from './ExpandedViewComponents';
import { renderCell } from '../grid/renderCell';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import actionsRender from '../actions/Actions';
import { separateActionsByLevel, separateColumnsByDisplayLevel, filterColumnsByHideIfEmpty } from '../helpFunctions';

interface AutoExpandedViewProps<T, U> {
  childRow: U | T;
  row: T;
  columns: ColumnDefinition<T>[];
  actions?: ActionDefinition<T>[];
  firstColumnRef?: React.RefObject<HTMLDivElement>;
  childIndex: number;
  desktopSideBottomColumns?: boolean;
  /** See `ExpandedConfig.panelLayout`. */
  panelLayout?: boolean;
}

export const AutoExpandedView = <T, U>({
  childRow,
  row,
  columns,
  actions,
  firstColumnRef: _firstColumnRef,
  childIndex: _childIndex,
  desktopSideBottomColumns = false,
  panelLayout = false
}: AutoExpandedViewProps<T, U>) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((t: Theme) => t.breakpoints.down('md'));
  const separatedColumns = useMemo(() => {
    const filteredColumns = filterColumnsByHideIfEmpty(columns, childRow, isSmallScreen);
    return separateColumnsByDisplayLevel(filteredColumns, isSmallScreen);
  }, [columns, childRow, isSmallScreen]);
  const [, regularActions] = useMemo(
    () => separateActionsByLevel(actions || [], isSmallScreen),
    [actions, isSmallScreen]
  );
  const standardColumns = separatedColumns['standard'];
  const expandedColumns = separatedColumns['expanded'];
  const topColumns = separatedColumns['top'];
  const bottomColumns = separatedColumns['bottom'];

  const contactFieldsOrder = useMemo(
    () => ['phone', 'mail', 'address', 'source', 'sourceDetails', 'check'] as const,
    []
  );
  // Any `standard`-level column not in this known contact-field whitelist still belongs in this
  // panel (it's simply every consumer using this layout today already limits itself to exactly
  // these fields) — appended after the prioritized ones instead of silently being dropped, so a
  // grid with different standard columns (e.g. forms' "type"/"activityArea") still shows them.
  const contactColumns = useMemo(() => {
    const byField = new Map(standardColumns?.map(col => [col.field, col]) ?? []);
    const prioritized = contactFieldsOrder.map(f => byField.get(f)).filter(Boolean) as ColumnDefinition<T>[];
    const prioritizedFields = new Set(prioritized.map(col => col.field));
    const rest = (standardColumns || []).filter(col => !prioritizedFields.has(col.field));
    return [...prioritized, ...rest];
  }, [standardColumns, contactFieldsOrder]);
 
  const commentColumn = useMemo(() => expandedColumns?.find(col => col.field === 'comment'), [expandedColumns]);

  const donationGroupName = useMemo(() => {
    const raw = (childRow as { groupName?: string | null })?.groupName;
    if (raw == null || raw === '') {
      return '';
    }
    return String(raw).trim();
  }, [childRow]);

  const donationGroupImage = useMemo(() => {
    const raw = (childRow as { groupImage?: string | null })?.groupImage;
    if (raw == null || raw === '') {
      return '';
    }
    return String(raw).trim();
  }, [childRow]);

  const mobileDonationsTopColumns = useMemo(() => {
    if (!isSmallScreen) {
      return [];
    }
    if (topColumns && topColumns.length > 0) {
      return topColumns;
    }
    if (!commentColumn && !panelLayout) {
      return [];
    }
    return columns.filter(col => col.show !== false && col.mobileDisplayLevel === 'top');
  }, [isSmallScreen, topColumns, columns, commentColumn, panelLayout]);

  const createColumns = (fields: ColumnDefinition<T>[], maxPerColumn = 3) => {
    if (!fields) return [];
    const cols = [];
    for (let i = 0; i < fields.length; i += maxPerColumn) {
      cols.push(fields.slice(i, i + maxPerColumn));
    }
    return cols;
  };

  const getColumnIcon = (col: ColumnDefinition<T>) => {
    switch (col.field) {
      case 'phone':
        return <PhoneOutlinedIcon />;
      case 'mail':
        return <MailOutlineIcon />;
      case 'address':
        return <LocationOnOutlinedIcon />;
      case 'source':
      case 'sourceDetails':
        return <CampaignOutlinedIcon />;
      case 'check':
        return <SendOutlinedIcon />;
    }
    return null;
  };

  const cellForChild = (col: ColumnDefinition<T>) =>
    renderCell(childRow, col, undefined, { preferMobileOverride: isSmallScreen });

  const bottomTextSx = {
    fontSize: '14px',
    lineHeight: 1.3,
    color: 'text.primary'
  };

  const bottomValueSx = {
    ...bottomTextSx,
    fontWeight: 500,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    minWidth: 0,
    width: '100%'
  };

  const donationGroupViaLine =
    donationGroupName.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minWidth: 0,
          width: '100%',
          alignItems: 'flex-start'
        }}
      >
        <Typography sx={{ ...bottomTextSx, fontWeight: 400, color: 'text.secondary' }}>נתרם דרך קבוצה:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
          <Avatar
            src={donationGroupImage || undefined}
            alt=""
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.7rem',
              flexShrink: 0,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText'
            }}
          >
            {getCampaignGroupInitials(donationGroupName)}
          </Avatar>
          <Typography sx={{ ...bottomValueSx, flex: 1 }}>{donationGroupName}</Typography>
        </Box>
      </Box>
    ) : null;

  const getStackedBottomLabel = (field: string, headerName: ColumnDefinition<T>['headerName']) => {
    if (field === 'branchSubBranch') return 'שם סניף';
    if (field === 'shaliachName') return 'שם שליח';
    return typeof headerName === 'string' ? headerName : String(headerName);
  };

  const stackedLayoutFields = new Set([
    'branchSubBranch',
    'shaliachName',
    'sourceType',
    'productName',
    'quantity',
    'check',
    'creditType'
  ]);

  const renderStackedColumn = (col: ColumnDefinition<T>, index: number, keyPrefix = '') => {
    const field = String(col.field);
    const value = cellForChild(col);
    return (
      <Box
        key={`${keyPrefix}${field}-${index}`}
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, width: '100%', alignItems: 'flex-start' }}
      >
        <Typography sx={{ ...bottomTextSx, fontWeight: 400, color: 'text.secondary' }}>
          {getStackedBottomLabel(field, col.headerName)}:
        </Typography>
        <Box sx={bottomValueSx}>{value}</Box>
      </Box>
    );
  };

  const renderButtomColumn = (col: ColumnDefinition<T>, index: number) => {
    const value = cellForChild(col);
    const field = String(col.field);

    if (field === 'feeAndNetChips') {
      return (
        <Box key={index} sx={{ width: '100%' }}>
          {value}
        </Box>
      );
    }

    if (stackedLayoutFields.has(field)) {
      return renderStackedColumn(col, index, 'bottom-');
    }

    if (typeof value === 'string') {
      return (
        <Chip
          key={index}
          size="small"
          label={
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  ...bottomTextSx,
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {col.headerName}:
              </Typography>
              <Typography sx={bottomValueSx}>{value}</Typography>
            </Box>
          }
        />
      );
    }

    return (
      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            ...bottomTextSx,
            fontWeight: 400,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {col.headerName}:
        </Typography>
        <Box sx={bottomValueSx}>{value}</Box>
      </Box>
    );
  };

  const standardColumnsGroups = createColumns(standardColumns, 3);

  const mainBg = theme.palette.grey[600];
  const railBg = theme.palette.grey[300];
  const isRtl = theme.direction === 'rtl';

  const renderEmptyValue = (label: string) => (
    <Typography
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: 'text.secondary',
        fontStyle: 'italic'
      }}
    >
      {`לא צוין ${(label as string).toLowerCase()}`}
    </Typography>
  );

  const mainPanel = (
    <ExpandedViewLayout>
      <Grid
        container
        item
        justifyContent="space-around"
        xs={12}
        md={Math.max((standardColumnsGroups?.length || 0) * 2, 3)}
        sx={{ width: '100%' }}
      >
        {topColumns && topColumns.length > 0 && (
          <Grid container spacing={2} item xs={12} sx={{ mb: 2 }} wrap="wrap">
            {topColumns.map((col, index) => (
              <Grid item xs={'auto'} key={index}>
                {cellForChild(col)}
              </Grid>
            ))}
          </Grid>
        )}
        {isSmallScreen && bottomColumns && bottomColumns.length > 0 && (
          <Grid item container spacing={2} xs={12} sx={{ mb: 2 }}>
            {bottomColumns?.map((col, index) => {
              return (
                <Grid item xs={12} key={index}>
                  {renderButtomColumn(col, index)}
                </Grid>
              );
            })}
          </Grid>
        )}
        {standardColumnsGroups?.map((columnGroup, groupIndex) => (
          <Grid item xs={12} md={4} key={`group-${groupIndex}`}>
            <Stack sx={{ height: '100%' }}>
              {columnGroup?.map((col, index) => {
                return (
                  <StandartItem
                    key={index}
                    icon={col?.icon || getColumnIcon(col)}
                    label={col.headerName}
                    value={cellForChild(col)}
                  />
                );
              })}
            </Stack>
          </Grid>
        ))}
      </Grid>

      <Grid item container xs={12} md={Math.max((expandedColumns?.length || 0) * 3, 4)}>
        {expandedColumns &&
          expandedColumns?.map((col, index) => (
            <Grid item xs={12} md={12 / expandedColumns.length} key={index}>
              <InfoItem
                icon={col?.icon || getColumnIcon(col) || <EditOutlinedIcon />}
                label={col.headerName}
                value={
                  cellForChild(col) || (
                    <Typography
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'text.secondary',
                        fontStyle: 'italic'
                      }}
                    >
                      {`לא צוין ${(col.headerName as string).toLowerCase()}`}
                    </Typography>
                  )
                }
              />
            </Grid>
          ))}
      </Grid>

      {!isSmallScreen && bottomColumns && bottomColumns.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              py: 2,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            {bottomColumns &&
              bottomColumns?.map((col, index) => {
                return renderButtomColumn(col, index);
              })}
          </Box>
        </Grid>
      )}
    </ExpandedViewLayout>
  );

  const expandedBottomSection =
    bottomColumns && bottomColumns.length > 0 ? (
      isSmallScreen ? (
        <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch' }}>
          {donationGroupViaLine}
          {bottomColumns.map((col, index) => (
            <Box key={index} sx={{ minWidth: 0, width: '100%' }}>
              {renderButtomColumn(col, index)}
            </Box>
          ))}
        </Stack>
      ) : desktopSideBottomColumns && commentColumn ? null : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            py: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%'
          }}
        >
          {bottomColumns.map((col, index) => renderButtomColumn(col, index))}
        </Box>
      )
    ) : null;

  const packDonationsLikeDesktopColumns =
    desktopSideBottomColumns && !isSmallScreen && Boolean(commentColumn) && Boolean(bottomColumns?.length);

  const donationsLikeDesktopBottomColumn = packDonationsLikeDesktopColumns ? (
      <Box
        sx={{
          order: 2,
          flex: '0 1 auto',
          minWidth: 150,
          maxWidth: 260,
          p: 2,
          marginInlineStart: 4,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}
      >
        <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'flex-start' }}>
          {donationGroupViaLine}
          {bottomColumns.map((col, index) => (
            <Box key={`side-bottom-${index}`} sx={{ minWidth: 0, width: '100%' }}>
              {renderButtomColumn(col, index)}
            </Box>
          ))}
        </Stack>
      </Box>
    ) : null;

  const donationsLikeTopSection =
    mobileDonationsTopColumns.length > 0 ? (
      <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch' }}>
        {mobileDonationsTopColumns.map((col, index) => renderStackedColumn(col, index, 'top-'))}
      </Stack>
    ) : null;

  const donationsLikeCommentBox = commentColumn ? (
    <Box
      sx={{
        bgcolor: railBg,
        borderRadius: '10px',
        p: 3,
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        ...(!isSmallScreen ? { height: '100%', flex: 1 } : {}),
        ...(isSmallScreen ? { p: 2, alignSelf: 'stretch' } : {})
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
        {/* Skip the title line entirely when empty (e.g. forms' own comment column, whose
            content already has its own per-item titles) — an empty Typography still reserves
            its line-height, pushing the first line of real content down for no reason. */}
        {commentColumn.headerName ? (
          <Typography sx={{ fontSize: '14px', lineHeight: 1.3, fontWeight: 400, color: 'text.secondary' }}>
            {typeof commentColumn.headerName === 'string' ? commentColumn.headerName : String(commentColumn.headerName)}
          </Typography>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {commentColumn.renderCell ? (
            commentColumn.renderCell(childRow as T)
          ) : (
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: 1.3,
                fontWeight: 500,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {(() => {
                const raw =
                  commentColumn.valueGetter?.(childRow as any) ??
                  (childRow as any)?.[commentColumn.field as keyof typeof childRow];
                const text = raw === null || raw === undefined ? '' : String(raw);
                return text.trim().length > 0 ? text : `לא צוין ${String(commentColumn.headerName).toLowerCase()}`;
              })()}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  ) : null;

  // Without the gray comment box (panelLayout only) there's nothing to align with, so every
  // item — top fields and details alike — gets the same 12px gap as the details list.
  const hasCommentBox = Boolean(commentColumn);

  const donationsLikePanel = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: hasCommentBox ? 2 : 1.5,
        alignItems: 'stretch'
      }}
    >
      {donationsLikeTopSection}
      {isSmallScreen && donationsLikeCommentBox}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          gap: packDonationsLikeDesktopColumns ? 4 : 3,
          alignItems: 'stretch',
          ...(!isSmallScreen && packDonationsLikeDesktopColumns ? { justifyContent: 'flex-start' } : {})
        }}
      >
        {/* Contact values (no label / no timeline SVG) */}
        <Box
          sx={{
            minWidth: 0,
            flex: packDonationsLikeDesktopColumns ? '0 1 auto' : 1,
            width: packDonationsLikeDesktopColumns ? 'fit-content' : undefined,
            maxWidth: packDonationsLikeDesktopColumns ? 'min(100%, 420px)' : undefined,
            order: isSmallScreen ? 0 : isRtl ? 1 : 0,
            // Mobile: sections are stacked, so every one shares the same (zero) inline inset.
            p: isSmallScreen ? 0 : 2,
            // Matches the gray comment box's own `p: 3` top padding so the first item's title in
            // each of the two side-by-side panels starts at the same height.
            pt: hasCommentBox && !isSmallScreen ? 3 : 0,
            pb: hasCommentBox && !isSmallScreen ? 2 : 0,
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            flexShrink: packDonationsLikeDesktopColumns ? 0 : undefined
          }}
        >
          <Stack sx={{ width: 'fit-content', maxWidth: '100%', flex: 1, justifyContent: 'flex-start' }}>
            {contactColumns.map((col, index) => {
              const field = String(col.field);
              // Fields outside the original contact-field whitelist (see contactColumns above)
              // usually have no matching icon — show a title instead of leaving an empty icon
              // slot, since there's nothing to convey what the value means otherwise.
              const hasIcon = field !== 'check' && Boolean(col?.icon || getColumnIcon(col));
              const fontWeight = field === 'mail' ? 700 : field === 'address' ? 600 : hasIcon ? 400 : 500;

              // Decide "empty" without relying on renderCell (which might return components even for empty strings).
              let rawForEmptyCheck: any;
              try {
                rawForEmptyCheck = col.valueGetter
                  ? col.valueGetter(childRow as any)
                  : (childRow as any)[col.field as keyof typeof childRow];
              } catch {
                rawForEmptyCheck = '';
              }
              if (Array.isArray(rawForEmptyCheck)) rawForEmptyCheck = rawForEmptyCheck.join(', ');
              if (rawForEmptyCheck && typeof rawForEmptyCheck === 'object') {
                try {
                  rawForEmptyCheck = JSON.stringify(rawForEmptyCheck);
                } catch {
                  rawForEmptyCheck = '';
                }
              }
              const emptyCheckString =
                rawForEmptyCheck === null || rawForEmptyCheck === undefined ? '' : String(rawForEmptyCheck);
              const isEmpty =
                emptyCheckString.trim().length === 0 || emptyCheckString === 'null' || emptyCheckString === 'undefined';

              // Special-case mailing approval: if missing, still render ❌ (no "לא צוין ...")
              const isMailingApproval = field === 'check';
              const rawValue = isMailingApproval ? cellForChild(col) : isEmpty ? '' : cellForChild(col);
              const valueNode =
                !isMailingApproval && (isEmpty || rawValue === '' || rawValue === null || rawValue === undefined) ? (
                  renderEmptyValue(String(col.headerName))
                ) : typeof rawValue === 'string' || typeof rawValue === 'number' ? (
                  <Typography sx={{ fontSize: '14px', lineHeight: 1.3, fontWeight }}>{rawValue}</Typography>
                ) : (
                  <Box
                    sx={{
                      fontSize: '14px',
                      lineHeight: 1.3,
                      fontWeight,
                      display: 'flex',
                      '& *': { fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit' },
                      '& a': { textDecoration: 'none' }
                    }}
                  >
                    {rawValue}
                  </Box>
                );

              return (
                <Box key={`${String(col.field)}-${index}`} sx={{ minWidth: 0 }}>
                  {field === 'check' && (
                    <Box
                      sx={{
                        my: 0.75,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        opacity: 0.7
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      // Icon-less items (title stacked above value) need less vertical padding
                      // between consecutive items than the single-line icon rows do — otherwise
                      // the fixed 32px row height leaves an oversized gap under two lines of text.
                      // `mb` (instead of `py`) matches the 12px item-to-item gap used by the gray
                      // content box (formsColumns.tsx's 'comment' column) for visual consistency.
                      py: !hasIcon && field !== 'check' ? 0 : 0.25,
                      mb: !hasIcon && field !== 'check' ? '12px' : 0,
                      minHeight: !hasIcon && field !== 'check' ? 'auto' : 32,
                      '& svg': { fontSize: 18 }
                    }}
                  >
                    {/* No icon for mailing approval, or for fields with no matching icon at all */}
                    {field !== 'check' && hasIcon && (
                      <Box sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                        {col?.icon || getColumnIcon(col)}
                      </Box>
                    )}
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        display: 'flex',
                        flexDirection: !hasIcon && field !== 'check' ? 'column' : 'row',
                        alignItems: !hasIcon && field !== 'check' ? 'flex-start' : 'center',
                        gap: field === 'check' ? 0.75 : !hasIcon ? '4px' : 1,
                        justifyContent: 'flex-start'
                      }}
                    >
                      {/* In RTL we want the approval mark on the "right" side, so render it first */}
                      {field === 'check' ? (
                        <>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {valueNode}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              lineHeight: 1.3,
                              fontWeight: 400,
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {col.headerName}
                          </Typography>
                        </>
                      ) : !hasIcon ? (
                        <>
                          <Typography
                            sx={{ fontSize: '14px', lineHeight: 1.3, fontWeight: 400, color: 'text.secondary' }}
                          >
                            {col.headerName}
                          </Typography>
                          {valueNode}
                        </>
                      ) : (
                        valueNode
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {!isSmallScreen && (
          <Box
            sx={{
              order: isRtl ? 0 : 1,
              flex: '0 1 40%',
              minWidth: 220,
              maxWidth: '50%',
              minHeight: 0,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {donationsLikeCommentBox}
          </Box>
        )}

        {donationsLikeDesktopBottomColumn}
      </Box>
      {expandedBottomSection}
    </Box>
  );

  const useDonationsLikeLayout = Boolean(commentColumn) || panelLayout;
  // The actions rail only shows when there are actions to put in it.
  const hasRailActions = Boolean(regularActions?.length);

  return (
    <Box
      sx={{
        display: 'grid',
        width: '100%',
        gridTemplateColumns: hasRailActions ? '1fr 50px' : '1fr',
        alignItems: 'stretch',
        minHeight: 1,
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          bgcolor: mainBg,
          py: useDonationsLikeLayout && !isSmallScreen ? 4 : 3,
          px: useDonationsLikeLayout && !isSmallScreen ? 3 : 2,
          minWidth: 0
        }}
      >
        {useDonationsLikeLayout ? donationsLikePanel : mainPanel}
      </Box>
      {hasRailActions && (
        <Box
          sx={{
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            boxSizing: 'border-box',
            bgcolor: railBg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 2,
            pb: 2,
            gap: 1
          }}
        >
          <Stack direction="column" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
            {actionsRender(regularActions, row, childRow)}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default AutoExpandedView;
